import { Switch } from '../../../../components';
import {
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelHeader4,
    LabelHeader5,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
    TextField,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { CheckCircle, CopyIcon } from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { PhoneMessageView } from '../../views/PhoneMessageView';
import { useState } from 'react';

const relationNameKey = 'relationNameKey';
const whatIsNeededKey = 'whatIsNeededKey';
const howToHelpKey = 'howToHelpKey';

function createMessage(
    relationName: string,
    whatIsNeeded: string,
    howToHelp: string,
): string[] {
    return [relationName && `Kære ${relationName}`, whatIsNeeded, howToHelp]
        .map((x) => x?.trim())
        .filter(Boolean);
}
export const exerciseData13: ExerciseData = {
    ExerciseView: Module1Exercise3,
    dataKeys: [relationNameKey, whatIsNeededKey, howToHelpKey],
    questions: [
        {
            dataKeys: [relationNameKey, whatIsNeededKey, howToHelpKey],
            summary: (exercise) =>
                createMessage(
                    exercise.Data[relationNameKey],
                    exercise.Data[whatIsNeededKey],
                    exercise.Data[howToHelpKey],
                ),
            Answers: ({ exercise, className }) => {
                const relationName = exercise.Data[relationNameKey];
                const whatIsNeeded = exercise.Data[whatIsNeededKey];
                const howToHelp = exercise.Data[howToHelpKey];
                return (
                    <div className={className}>
                        <LabelDisplay1>
                            Din besked{relationName && ` til ${relationName}`}
                        </LabelDisplay1>
                        <LabelBody className="mt-10 whitespace-pre-wrap">
                            {createMessage(
                                relationName,
                                whatIsNeeded,
                                howToHelp,
                            ).join('\n')}
                        </LabelBody>
                    </div>
                );
            },
        },
    ],
};

function Module1Exercise3({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Sig det højt">
            <ProgressBar current={exercise.CurrentPage} total={3} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                })
                            }
                        />
                    ),
                    2: (
                        <Page2
                            relationName={exercise.Data[relationNameKey] ?? ''}
                            whatIsNeeded={exercise.Data[whatIsNeededKey] ?? ''}
                            howToHelp={exercise.Data[howToHelpKey] ?? ''}
                            onContinue={(data) =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [relationNameKey]: normalizeInput(
                                            data.relationName,
                                        ),
                                        [whatIsNeededKey]: normalizeInput(
                                            data.whatIsNeeded,
                                        ),
                                        [howToHelpKey]: normalizeInput(
                                            data.howToHelp,
                                        ),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            relationName={exercise.Data[relationNameKey] ?? ''}
                            whatIsNeeded={exercise.Data[whatIsNeededKey] ?? ''}
                            howToHelp={exercise.Data[howToHelpKey] ?? ''}
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '1.completed',
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

function Page1({ onContinue }: { onContinue: () => void }) {
    return (
        <>
            <LabelHeader4>
                Fortynd det svære ved at dele det med nogen
            </LabelHeader4>
            <LabelBody class="mt-10">
                Vi ved, at det kan være overvældende at fortælle folk, at man er
                under pres. Vi vil derfor gerne hjælpe dig lidt på vej. Denne
                øvelse går ud på at formulere, hvordan du gerne vil hjælpes.
                Hvis du kan formulere det, så er det nemmere for andre at hjælpe
                dig. For nogen er det en hjælp at vende det svære, mens det for
                andre kan være en lige så stor hjælp at holde en pause fra dét,
                der er svært. Du ved bedst, hvad der hjælper dig.
            </LabelBody>
            <SwirlyBox class="my-30">Er du klar til at gå i gang?</SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Start øvelsen
            </SecondaryButton>
        </>
    );
}

type Page2Props = {
    relationName: string;
    whatIsNeeded: string;
    howToHelp: string;
    onContinue: (data: {
        relationName: string;
        whatIsNeeded: string;
        howToHelp: string;
    }) => void;
};
function Page2({
    relationName: _relationName,
    whatIsNeeded: _whatIsNeeded,
    howToHelp: _howToHelp,
    onContinue,
}: Page2Props) {
    const [relationName, setRelationName] = useSyncedState(_relationName);
    const [whatIsNeeded, setWhatIsNeeded] = useSyncedState(_whatIsNeeded);
    const [howToHelp, setHowToHelp] = useSyncedState(_howToHelp);
    return (
        <>
            <Card>
                <LabelHeader5>
                    Start med at udvælge en fra din relationscirkel
                </LabelHeader5>
                <LabelBody class="mt-10">
                    Det kan være en tæt relation eller en relation, som du gerne
                    vil bringe tættere på dig.
                </LabelBody>
                <TextField
                    placeholder="Skriv et navn..."
                    text={relationName}
                    onChangeText={setRelationName}
                />
                <LabelBodyHeadline color="text-dark-red" class="mb-10 mt-30">
                    Hvad har du brug for hjælp til?
                </LabelBodyHeadline>
                <LabelBody>
                    Prøv at formulere hvad dit konkrete behov er. F.eks. “Jeg
                    bliver ofte overvældet af alt det, jeg skal og jeg har behov
                    for at dele det med nogen.”
                </LabelBody>
                <LabelBody class="mt-10">
                    Det kan også være, at du har brug for at bede om hjælp til
                    at lade det svære fylde mindre, f.eks. "Jeg savner, at vi
                    kan være sammen om noget af det, der går godt."
                </LabelBody>
                <Textarea text={whatIsNeeded} onChangeText={setWhatIsNeeded} />
                <LabelBodyHeadline color="text-dark-red" class="mb-10 mt-30">
                    Hvordan vil du gerne hjælpes?
                </LabelBodyHeadline>
                <LabelBody>
                    F.eks. “Jeg synes, at du er virkelig god til at lytte og
                    forstå mig, og jeg har virkelig brug for at tale med en, som
                    vil lytte uden at komme med forslag. Vil du være det for
                    mig?”
                </LabelBody>
                <LabelBody class="mt-10">
                    Eller: "Jeg vil gerne sige til, når jeg har brug for at
                    snakke frem for, at du ofte spørger ind til mig."
                </LabelBody>
                <Textarea text={howToHelp} onChangeText={setHowToHelp} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton
                onClick={() =>
                    onContinue({
                        relationName,
                        whatIsNeeded,
                        howToHelp,
                    })
                }
            >
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

const placeholderMessage = `Kære...
Jeg bliver ofte overvældet af alt det jeg skal og jeg har behov for at dele det med nogen.
Jeg synes du er virkelig god til at lytte og forstå mig, og jeg har virkelig brug for at tale med en som vil lytte uden at komme med forslag. Vil du være det for mig?`;

type Page3Props = {
    relationName: string;
    whatIsNeeded: string;
    howToHelp: string;
    onContinue: () => void;
};
function Page3({
    relationName,
    whatIsNeeded,
    howToHelp,
    onContinue,
}: Page3Props) {
    const message =
        createMessage(relationName, whatIsNeeded, howToHelp).join('\n') ||
        placeholderMessage;

    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard
            .writeText(message)
            .then(() => setCopied(true))
            .catch((e) => {
                alert('Kopiering kunne ikke gennemføres\n' + e.message);
            });
    };

    return (
        <>
            <LabelHeader5>Besked til en du står nær</LabelHeader5>
            <LabelBody class="mt-10">
                Her har vi samlet din besked, så den er klar til at blive
                kopieret og sendt afsted. Det kan være, at det virker for
                drastisk lige nu. Bare rolig, den er gemt, så du kan sende den
                afsted senere.
            </LabelBody>
            <PhoneMessageView class="mx-auto mt-30" message={message}>
                <div className="mt-20 flex flex-col items-center">
                    <GhostButton className="flex" onClick={handleCopy}>
                        <CopyIcon className="mr-10" /> Kopier besked
                    </GhostButton>

                    <div className="mt-20 flex flex-row items-center">
                        <CheckCircle
                            className={`mr-10 text-success ${!copied ? 'invisible' : ''}`}
                        />
                        <LabelBodyHeadline
                            className={!copied ? 'invisible' : ''}
                        >
                            Beskeden er kopieret
                        </LabelBodyHeadline>
                    </div>
                </div>
            </PhoneMessageView>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}
